import {
  Stack,
  Grid,
  Typography,
  Box,
  Paper,
  Avatar,
  alpha,
  Fade,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Link,
  IconButton,
  Tooltip,
  Badge,
  CircularProgress
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PastInteraction } from "./types/businessInsights";
import Iconify from "../../../minimals/components/iconify";
import { fMonthDayYearSlashNotation } from "../../utils/formatTime";
import { LineChart, LineSeriesType } from "@mui/x-charts";
import ContactInfo from "./ContactInfo";
import { getLogo } from "../../utils/getLogo";
import { TitleLogo } from "./TitleLogo";
import { SpecsLogo } from "./SpecsLogo";
import { ExchangeLogo } from "./ExchangeLogo";
import TBULogo from "./TBULogo";
import { stringAvatar } from "./utils/avatarHelpers";
import { ContactSummary, CompanySummary, ChartRow } from "./types/businessInsights";
import { MetricCard } from "./MetricCard";
import { PastInteractionsModal } from "./PastInteractionsModal";

type SummaryCardProps = {
  kind: "Contact" | "Company";
  summary: ContactSummary | CompanySummary;
  onCompanyClick?: (companyId: number, companyName: string) => void;
};

type SortField =
  | "orderNumber"
  | "receivedDate"
  | "closedDate"
  | "sourceCompany"
  | "status"
  | "propertyState"
  | "dealAmount";
type SortDirection = "asc" | "desc";

export function SummaryCard({ kind, summary, onCompanyClick }: SummaryCardProps) {
  const theme = useTheme();
  const [focusedBusiness, setFocusedBusiness] = useState<"Title" | "Specs" | "Exchange" | null>(null);
  const [expandOrderDetails, setExpandOrderDetails] = useState(false);
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [expandMembers, setExpandMembers] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [hoveredQuarter, setHoveredQuarter] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>("closedDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [pastInteractionsOpen, setPastInteractionsOpen] = useState(false);
  const [interactionsCount, setInteractionsCount] = useState(0);

  const phonebookContactId = (summary as ContactSummary).phonebookContactId;
  const companyId = (summary as CompanySummary).phonebookCompanyId;

  type CompanyMember = {
    contactId: number;
    name: string;
    role: string;
    email: string;
    phones: { type: string; number: string }[];
  };

  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [isFetchingMembers, setIsFetchingMembers] = useState(false);

  useEffect(() => {
    if (kind !== "Company" || !companyId) return;
    setIsFetchingMembers(true);
    axios
      .get<CompanyMember[]>(`/api/clientphonebook/companies/getContacts?id=${companyId}&onlyShowActive=true`)
      .then(({ data }) => setMembers(data))
      .catch(() => setMembers([]))
      .finally(() => setIsFetchingMembers(false));
  }, [kind, companyId]);

  const formatPhone = (phones: { type: string; number: string }[]) => {
    const biz = phones?.find((p) => p.type === "Business")?.number ?? phones?.[0]?.number ?? "";
    const cleaned = biz.replace(/\D/g, "");
    const m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : biz;
  };

  useEffect(() => {
    const fetchInteractionsCount = async () => {
      if (kind !== "Contact" || !phonebookContactId) return;

      try {
        const { data } = await axios.get<PastInteraction[]>(
          "/api/clientphonebook/contactInteractions/getPastInteractions",
          { params: { contactId: phonebookContactId } }
        );
        setInteractionsCount(data.length);
      } catch (err) {
        console.error("Error fetching interactions count:", err);
      }
    };

    fetchInteractionsCount();
  }, [kind, phonebookContactId]);

  const chartData: ChartRow[] = useMemo(() => {
    if (!summary.timeline?.length) return [];

    // Group by quarter/year and aggregate
    const groupedData = summary.timeline.reduce((acc, pt) => {
      const key = `Q${pt.calendarQuarterNumber} ${pt.calendarYearNumber}`;
      if (!acc[key]) {
        acc[key] = {
          label: key,
          Title: 0,
          Specs: 0,
          Exchange: 0,
          DealAmount: 0,
          year: pt.calendarYearNumber,
          quarter: pt.calendarQuarterNumber
        };
      }
      acc[key].Title += pt.titleOrderCount;
      acc[key].Specs += pt.npvCount;
      acc[key].Exchange += pt.exchangeOrderCount;
      acc[key].DealAmount += pt.dealAmount ?? 0;
      return acc;
    }, {} as Record<string, ChartRow & { year: number; quarter: number }>);

    // Sort by year and quarter
    return Object.values(groupedData).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.quarter - b.quarter;
    });
  }, [summary]);

  const contactInfo = useMemo(
    () => ({
      email: summary.email ?? "",
      phone: summary.phone ?? "",
      city: summary.city ?? "",
      state: summary.state ?? ""
    }),
    [summary]
  );

  const displayName = (summary as ContactSummary).fullName ?? (summary as CompanySummary).companyName ?? kind;
  const preferredName = (summary as ContactSummary).preferredName;
  const emailDomain = summary.email?.split("@")[1];
  const isTbuContact = (summary as ContactSummary).isTbuContact;
  const isTbuCompany = (summary as CompanySummary).isTbuCompany;
  const isTbu = isTbuContact === "Yes" || isTbuCompany === "Yes";

  const hasTitle = (summary.titleOpenOrderCount ?? 0) + (summary.titleClosedOrderCount ?? 0) > 0;
  const hasSpecs = (summary.openNpvCount ?? 0) + (summary.closedNpvCount ?? 0) > 0;
  const hasExchange = (summary.exchangeOpenOrderCount ?? 0) + (summary.exchangeClosedOrderCount ?? 0) > 0;
  const hasBusinessData = hasTitle || hasSpecs || hasExchange;
  const businessCount = [hasTitle, hasSpecs, hasExchange].filter(Boolean).length;
  const hasMultipleBusinesses = businessCount > 1;

  const chartSeries = useMemo<LineSeriesType[]>(() => {
    const series: LineSeriesType[] = [];

    if (hasTitle && (!focusedBusiness || focusedBusiness === "Title")) {
      series.push({
        type: "line",
        id: "title",
        label: "Title",
        dataKey: "Title",
        color: theme.palette.primary.main,
        showMark: false,
        curve: "catmullRom",
        area: true,
        highlightScope: { highlight: "item", fade: "global" }
      });
    }

    if (hasSpecs && (!focusedBusiness || focusedBusiness === "Specs")) {
      series.push({
        type: "line",
        id: "specs",
        label: "Specs",
        dataKey: "Specs",
        color: theme.palette.success.main,
        showMark: false,
        curve: "catmullRom",
        area: true,
        highlightScope: { highlight: "item", fade: "global" }
      });
    }

    if (hasExchange && (!focusedBusiness || focusedBusiness === "Exchange")) {
      series.push({
        type: "line",
        id: "exchange",
        label: "Exchange",
        dataKey: "Exchange",
        color: theme.palette.warning.main,
        showMark: false,
        curve: "catmullRom",
        area: true,
        highlightScope: { highlight: "item", fade: "global" }
      });
    }

    return series;
  }, [
    chartData.length,
    hasExchange,
    hasSpecs,
    hasTitle,
    focusedBusiness,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main
  ]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Iconify icon="eva:chevron-down-outline" width={16} sx={{ opacity: 0.3 }} />;
    }
    return (
      <Iconify
        icon={sortDirection === "asc" ? "eva:arrow-up-fill" : "eva:arrow-down-fill"}
        width={16}
        sx={{ color: theme.palette.primary.main }}
      />
    );
  };

  return (
    <Fade in timeout={500}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === "light" ? "#fff" : theme.palette.background.paper,
            boxShadow: theme.palette.mode === "light" ? "0 8px 32px rgba(0,0,0,0.08)" : "0 8px 32px rgba(0,0,0,0.3)"
          }}
        >
          <Box
            sx={{
              background:
                theme.palette.mode === "light"
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              p: 2,
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Stack spacing={isMobile && isTbu ? 1.5 : 0} sx={{ position: "relative", zIndex: 1 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  {...stringAvatar(displayName)}
                  src={getLogo(emailDomain)}
                  sx={{
                    width: 48,
                    height: 48,
                    border: `2px solid ${alpha("#fff", 0.3)}`,
                    boxShadow: `0 4px 12px ${alpha("#000", 0.2)}`
                  }}
                />
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h5" fontWeight={800} sx={{ color: "#fff", letterSpacing: -0.3 }}>
                      {displayName}
                    </Typography>
                    {preferredName && (
                      <Typography
                        variant="body2"
                        sx={{ color: alpha("#fff", 0.9), fontWeight: 600, fontStyle: "italic" }}
                      >
                        {preferredName}
                      </Typography>
                    )}
                  </Stack>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 0.2 : 1}
                    alignItems={isMobile ? "left" : "center"}
                    flexWrap="wrap"
                  >
                    {(summary.city || summary.state) && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Iconify icon="eva:pin-fill" width={13} sx={{ color: alpha("#fff", 0.85) }} />
                        <Typography variant="caption" sx={{ color: alpha("#fff", 0.85), fontWeight: 600 }}>
                          {[summary.city, summary.state].filter(Boolean).join(", ")}
                        </Typography>
                      </Stack>
                    )}
                    {summary.lastOrderedDateCrossCompany && (
                      <>
                        {!isMobile && (summary.city || summary.state) && (
                          <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: alpha("#fff", 0.5) }} />
                        )}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Iconify icon="eva:clock-fill" width={13} sx={{ color: alpha("#fff", 0.85) }} />
                          <Typography variant="caption" sx={{ color: alpha("#fff", 0.85), fontWeight: 600 }}>
                            Last order {fMonthDayYearSlashNotation(new Date(summary.lastOrderedDateCrossCompany))}
                            {summary.lastOrderedCompany && ` with ${summary.lastOrderedCompany}`}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Stack>
                {isTbu && !isMobile && <TBULogo height={80} />}
              </Stack>
              {kind === "Contact" && phonebookContactId && (
                <Tooltip title="View Past Interactions">
                  <Badge
                    badgeContent={interactionsCount}
                    color="info"
                    max={99}
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      "& .MuiBadge-badge": {
                        fontSize: "0.65rem",
                        height: 18,
                        minWidth: 18,
                        fontWeight: 700
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => setPastInteractionsOpen(true)}
                      sx={{
                        bgcolor: alpha("#fff", 0.15),
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.25)
                        }
                      }}
                    >
                      <Iconify icon="mdi:history" width={22} sx={{ color: "#fff" }} />
                    </IconButton>
                  </Badge>
                </Tooltip>
              )}
              {isTbu && isMobile && (
                <Box sx={{ display: "flex", justifyContent: "flex-start", pt: 0.5, pl: 8 }}>
                  <TBULogo height={48} />
                </Box>
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              {hasBusinessData ? (
                <Grid
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: hasTitle && hasSpecs && hasExchange ? "1fr" : "1fr 1fr",
                      md: "repeat(3, 1fr)"
                    },
                    gap: 1.5
                  }}
                >
                  {hasTitle && (
                    <Grid item>
                      <MetricCard
                        icon={<TitleLogo height={20} />}
                        label="Title"
                        openValue={summary.titleOpenOrderCount ?? 0}
                        closedValue={summary.titleClosedOrderCount ?? 0}
                        color={theme.palette.primary.main}
                      />
                    </Grid>
                  )}
                  {hasSpecs && (
                    <Grid item>
                      <MetricCard
                        icon={<SpecsLogo height={20} />}
                        label="Specs"
                        openValue={summary.openNpvCount ?? 0}
                        closedValue={summary.closedNpvCount ?? 0}
                        color={theme.palette.success.main}
                      />
                    </Grid>
                  )}
                  {hasExchange && (
                    <Grid item>
                      <MetricCard
                        icon={<ExchangeLogo height={20} />}
                        label="Exchange"
                        openValue={summary.exchangeOpenOrderCount ?? 0}
                        closedValue={summary.exchangeClosedOrderCount ?? 0}
                        color={theme.palette.warning.main}
                      />
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: "center",
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 1.5
                  }}
                >
                  <Iconify icon="eva:info-outline" width={28} sx={{ color: theme.palette.info.main, mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    No business data available
                  </Typography>
                </Paper>
              )}

              {chartData.length > 0 && chartSeries.length > 0 && (
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 3,
                          height: 14,
                          borderRadius: 1,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        }}
                      />
                      <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: -0.2, fontSize: "0.875rem" }}>
                        Order History Timeline
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.65rem" }}>
                      Click quarter to filter orders
                    </Typography>
                  </Stack>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.25,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      transition: "all 0.2s ease",
                      "&:hover": {
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
                      }
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                      sx={{
                        mb: 1.5,
                        flexWrap: "wrap",
                        gap: 1,
                        p: 1,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        borderRadius: 1.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                      }}
                    >
                      {hasMultipleBusinesses && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          onClick={() => setFocusedBusiness(null)}
                          sx={{
                            cursor: "pointer",
                            userSelect: "none",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.25,
                            transition: "all 0.2s",
                            bgcolor: !focusedBusiness ? alpha(theme.palette.grey[500], 0.15) : "transparent",
                            border: !focusedBusiness
                              ? `1.5px solid ${alpha(theme.palette.grey[500], 0.3)}`
                              : `1.5px solid transparent`,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.grey[500], 0.12),
                              boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.15)}`
                            },
                            opacity: focusedBusiness ? 0.4 : 1
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: !focusedBusiness ? 700 : 600,
                              color: theme.palette.text.primary
                            }}
                          >
                            All
                          </Typography>
                        </Stack>
                      )}
                      {hasTitle && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          onClick={() => setFocusedBusiness(focusedBusiness === "Title" ? null : "Title")}
                          sx={{
                            cursor: "pointer",
                            userSelect: "none",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.25,
                            transition: "all 0.2s",
                            bgcolor:
                              focusedBusiness === "Title" ? alpha(theme.palette.primary.main, 0.15) : "transparent",
                            border:
                              focusedBusiness === "Title"
                                ? `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`
                                : `1.5px solid transparent`,
                            opacity: focusedBusiness && focusedBusiness !== "Title" ? 0.4 : 1,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: focusedBusiness === "Title" ? 700 : 600,
                              color: theme.palette.primary.main
                            }}
                          >
                            Title
                          </Typography>
                          {!isMobile && (
                            <Chip
                              label={(summary.titleOpenOrderCount ?? 0) + (summary.titleClosedOrderCount ?? 0)}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor:
                                  focusedBusiness === "Title"
                                    ? alpha(theme.palette.primary.main, 0.2)
                                    : alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.dark,
                                "& .MuiChip-label": { px: 0.75 }
                              }}
                            />
                          )}
                        </Stack>
                      )}
                      {hasSpecs && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          onClick={() => setFocusedBusiness(focusedBusiness === "Specs" ? null : "Specs")}
                          sx={{
                            cursor: "pointer",
                            userSelect: "none",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.25,
                            transition: "all 0.2s",
                            bgcolor:
                              focusedBusiness === "Specs" ? alpha(theme.palette.success.main, 0.15) : "transparent",
                            border:
                              focusedBusiness === "Specs"
                                ? `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`
                                : `1.5px solid transparent`,
                            opacity: focusedBusiness && focusedBusiness !== "Specs" ? 0.4 : 1,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.success.main, 0.12),
                              boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}`
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: focusedBusiness === "Specs" ? 700 : 600,
                              color: theme.palette.success.main
                            }}
                          >
                            Specs
                          </Typography>
                          {!isMobile && (
                            <Chip
                              label={(summary.openNpvCount ?? 0) + (summary.closedNpvCount ?? 0)}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor:
                                  focusedBusiness === "Specs"
                                    ? alpha(theme.palette.success.main, 0.2)
                                    : alpha(theme.palette.success.main, 0.1),
                                color: theme.palette.success.dark,
                                "& .MuiChip-label": { px: 0.75 }
                              }}
                            />
                          )}
                        </Stack>
                      )}
                      {hasExchange && (
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          onClick={() => setFocusedBusiness(focusedBusiness === "Exchange" ? null : "Exchange")}
                          sx={{
                            cursor: "pointer",
                            userSelect: "none",
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.25,
                            transition: "all 0.2s",
                            bgcolor:
                              focusedBusiness === "Exchange" ? alpha(theme.palette.warning.main, 0.15) : "transparent",
                            border:
                              focusedBusiness === "Exchange"
                                ? `1.5px solid ${alpha(theme.palette.warning.main, 0.4)}`
                                : `1.5px solid transparent`,
                            opacity: focusedBusiness && focusedBusiness !== "Exchange" ? 0.4 : 1,
                            "&:hover": {
                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                              boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.2)}`
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              fontWeight: focusedBusiness === "Exchange" ? 700 : 600,
                              color: theme.palette.warning.main
                            }}
                          >
                            Exchange
                          </Typography>
                          {!isMobile && (
                            <Chip
                              label={(summary.exchangeOpenOrderCount ?? 0) + (summary.exchangeClosedOrderCount ?? 0)}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor:
                                  focusedBusiness === "Exchange"
                                    ? alpha(theme.palette.warning.main, 0.2)
                                    : alpha(theme.palette.warning.main, 0.1),
                                color: theme.palette.warning.dark,
                                "& .MuiChip-label": { px: 0.75 }
                              }}
                            />
                          )}
                        </Stack>
                      )}
                    </Stack>
                    <LineChart
                      dataset={chartData}
                      xAxis={[
                        {
                          dataKey: "label",
                          scaleType: "point",
                          tickLabelStyle: {
                            fontSize: 11,
                            fontWeight: 600
                          }
                        }
                      ]}
                      yAxis={[
                        {
                          valueFormatter: (v: number | null) => (v == null ? "" : `${v} orders`),
                          tickLabelStyle: {
                            fontSize: 11,
                            fontWeight: 600
                          }
                        }
                      ]}
                      series={chartSeries}
                      height={200}
                      grid={{ horizontal: true }}
                      onAxisClick={(event, data) => {
                        if (data?.axisValue != null) {
                          const quarterLabel = String(data.axisValue);
                          setHoveredQuarter(hoveredQuarter === quarterLabel ? null : quarterLabel);
                        }
                      }}
                      sx={{
                        "& .MuiLineElement-root": {
                          strokeWidth: 2.5
                        },
                        "& .MuiMarkElement-root": {
                          scale: "0.8",
                          strokeWidth: 2
                        },
                        "& .MuiAreaElement-root": {
                          fillOpacity: 0.1
                        },
                        "& .MuiChartsLegend-root": {
                          display: "none"
                        }
                      }}
                    />
                  </Paper>
                </Box>
              )}

              {summary.timeline && summary.timeline.length > 0 && summary.timeline.some((t) => t.orderNumber) && (
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 3,
                          height: 14,
                          borderRadius: 1,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        }}
                      />
                      <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: -0.2, fontSize: "0.875rem" }}>
                        Order Details
                      </Typography>
                      {hoveredQuarter && (
                        <Chip
                          label={hoveredQuarter}
                          size="small"
                          onDelete={() => setHoveredQuarter(null)}
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                            color: theme.palette.info.dark
                          }}
                        />
                      )}
                    </Stack>
                    <Box
                      onClick={() => setExpandOrderDetails(!expandOrderDetails)}
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.text.secondary }}>
                        {expandOrderDetails ? "Collapse" : "Expand"}
                      </Typography>
                      <Iconify
                        icon={expandOrderDetails ? "eva:collapse-outline" : "eva:expand-outline"}
                        width={16}
                        sx={{ color: theme.palette.text.secondary }}
                      />
                    </Box>
                  </Stack>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      overflow: "hidden"
                    }}
                  >
                    <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                      <TextField
                        size="small"
                        placeholder="Search orders..."
                        value={orderSearchQuery}
                        onChange={(e) => setOrderSearchQuery(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                            bgcolor: theme.palette.background.paper,
                            "& fieldset": {
                              borderColor: theme.palette.divider
                            },
                            "&:hover fieldset": {
                              borderColor: theme.palette.primary.main
                            }
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Iconify
                                icon="eva:search-outline"
                                width={18}
                                sx={{ color: theme.palette.text.secondary }}
                              />
                            </InputAdornment>
                          ),
                          endAdornment: orderSearchQuery && (
                            <InputAdornment position="end">
                              <Box
                                onClick={() => setOrderSearchQuery("")}
                                sx={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  "&:hover": { opacity: 0.7 }
                                }}
                              >
                                <Iconify
                                  icon="eva:close-outline"
                                  width={18}
                                  sx={{ color: theme.palette.text.secondary }}
                                />
                              </Box>
                            </InputAdornment>
                          )
                        }}
                        fullWidth
                      />
                    </Box>
                    <TableContainer
                      sx={{
                        maxHeight: expandOrderDetails ? "none" : 200,
                        overflow: "auto",
                        transition: "max-height 0.3s ease"
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              onClick={() => handleSort("orderNumber")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>Order #</span>
                                {getSortIcon("orderNumber")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              onClick={() => handleSort("receivedDate")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>Received</span>
                                {getSortIcon("receivedDate")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              onClick={() => handleSort("closedDate")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>Closed Date</span>
                                {getSortIcon("closedDate")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              onClick={() => handleSort("sourceCompany")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>Source</span>
                                {getSortIcon("sourceCompany")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              onClick={() => handleSort("status")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>Status</span>
                                {getSortIcon("status")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              onClick={() => handleSort("propertyState")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <span>State</span>
                                {getSortIcon("propertyState")}
                              </Stack>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={() => handleSort("dealAmount")}
                              sx={{
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                cursor: "pointer",
                                userSelect: "none",
                                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.12) }
                              }}
                            >
                              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                <span>Deal Amount</span>
                                {getSortIcon("dealAmount")}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.timeline
                            .filter((order) => order.orderNumber)
                            .filter((order) => {
                              if (!focusedBusiness) return true;
                              if (focusedBusiness === "Title" && order.titleOrderCount > 0) return true;
                              if (focusedBusiness === "Specs" && order.npvCount > 0) return true;
                              if (focusedBusiness === "Exchange" && order.exchangeOrderCount > 0) return true;
                              return false;
                            })
                            .filter((order) => {
                              if (!hoveredQuarter) return true;
                              const orderQuarterLabel = `Q${order.calendarQuarterNumber} ${order.calendarYearNumber}`;
                              return orderQuarterLabel === hoveredQuarter;
                            })
                            .filter((order) => {
                              if (!orderSearchQuery) return true;
                              const query = orderSearchQuery.toLowerCase();
                              return (
                                order.orderNumber?.toLowerCase().includes(query) ||
                                order.sourceCompany?.toLowerCase().includes(query) ||
                                order.status?.toLowerCase().includes(query) ||
                                order.propertyState?.toLowerCase().includes(query) ||
                                order.dealAmount?.toString().includes(orderSearchQuery)
                              );
                            })
                            .sort((a, b) => {
                              let comparison = 0;

                              switch (sortField) {
                                case "orderNumber":
                                  comparison = (a.orderNumber || "").localeCompare(b.orderNumber || "");
                                  break;
                                case "receivedDate":
                                  const receivedA = a.receivedDate ? new Date(a.receivedDate).getTime() : 0;
                                  const receivedB = b.receivedDate ? new Date(b.receivedDate).getTime() : 0;
                                  comparison = receivedA - receivedB;
                                  break;
                                case "closedDate":
                                  const dateA = a.closedDate ? new Date(a.closedDate).getTime() : 0;
                                  const dateB = b.closedDate ? new Date(b.closedDate).getTime() : 0;
                                  comparison = dateA - dateB;
                                  break;
                                case "sourceCompany":
                                  comparison = (a.sourceCompany || "").localeCompare(b.sourceCompany || "");
                                  break;
                                case "status":
                                  comparison = (a.status || "").localeCompare(b.status || "");
                                  break;
                                case "propertyState":
                                  comparison = (a.propertyState || "").localeCompare(b.propertyState || "");
                                  break;
                                case "dealAmount":
                                  comparison = (a.dealAmount ?? 0) - (b.dealAmount ?? 0);
                                  break;
                              }

                              return sortDirection === "asc" ? comparison : -comparison;
                            })
                            .map((order, idx) => (
                              <TableRow
                                key={`${order.orderNumber}-${idx}`}
                                sx={{
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.04)
                                  }
                                }}
                              >
                                <TableCell>
                                  {order.titleOrderCount > 0 ? (
                                    <Link
                                      href={`https://internal.mcres.com/new-home/${order.orderNumber}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        textDecoration: "underline",
                                        color: "#212B36"
                                      }}
                                    >
                                      {order.orderNumber}
                                    </Link>
                                  ) : (
                                    <Typography
                                      sx={{
                                        fontFamily: "monospace",
                                        fontWeight: 600,
                                        fontSize: "0.875rem"
                                      }}
                                    >
                                      {order.orderNumber}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {order.receivedDate
                                      ? fMonthDayYearSlashNotation(new Date(order.receivedDate))
                                      : "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {order.closedDate ? fMonthDayYearSlashNotation(new Date(order.closedDate)) : "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                    {order.sourceCompany || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={order.status || "Unknown"}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      bgcolor:
                                        order.status === "Canceled"
                                          ? alpha(theme.palette.error.main, 0.12)
                                          : order.status === "Transferred"
                                          ? alpha(theme.palette.grey[500], 0.12)
                                          : order.status === "In Process"
                                          ? alpha(theme.palette.info.main, 0.12)
                                          : alpha(theme.palette.success.main, 0.12),
                                      color:
                                        order.status === "Canceled"
                                          ? theme.palette.error.dark
                                          : order.status === "Transferred"
                                          ? theme.palette.grey[700]
                                          : order.status === "In Process"
                                          ? theme.palette.info.dark
                                          : theme.palette.success.dark
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={600}>
                                    {order.propertyState || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                                    {order.dealAmount != null ? `$${order.dealAmount.toLocaleString()}` : "-"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Box>
              )}

              <Box>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      borderRadius: 1,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    }}
                  />
                  <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: -0.2 }}>
                    Contact Information
                  </Typography>
                </Stack>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`
                    }
                  }}
                >
                  {kind === "Contact" &&
                    (summary as ContactSummary).company &&
                    (summary as ContactSummary).dimDynamicsCompanyId && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Iconify icon="mdi:domain" width={18} />
                        <Box
                          component="span"
                          onClick={() => {
                            const contactSummary = summary as ContactSummary;
                            if (contactSummary.dimDynamicsCompanyId && contactSummary.company && onCompanyClick) {
                              onCompanyClick(contactSummary.dimDynamicsCompanyId, contactSummary.company);
                            }
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            cursor: "pointer",
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": {
                              textDecoration: "underline"
                            }
                          }}
                        >
                          {(summary as ContactSummary).company}
                          <Iconify icon="eva:external-link-outline" width={16} sx={{ opacity: 0.7 }} />
                        </Box>
                      </Stack>
                    )}
                  <ContactInfo contactInfo={contactInfo} />
                </Paper>
              </Box>

              {kind === "Company" && (
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Box
                        sx={{
                          width: 3,
                          height: 16,
                          borderRadius: 1,
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: -0.2 }}>
                        Company Members
                      </Typography>
                      {!isFetchingMembers && members.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({members.length})
                        </Typography>
                      )}
                    </Stack>
                    {!isFetchingMembers && members.length > 0 && (
                      <Box
                        onClick={() => setExpandMembers(!expandMembers)}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          transition: "all 0.2s",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} sx={{ color: theme.palette.text.secondary }}>
                          {expandMembers ? "Collapse" : "Expand"}
                        </Typography>
                        <Iconify
                          icon={expandMembers ? "eva:collapse-outline" : "eva:expand-outline"}
                          width={16}
                          sx={{ color: theme.palette.text.secondary }}
                        />
                      </Box>
                    )}
                  </Stack>
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.background.default, 0.5),
                      overflow: "hidden"
                    }}
                  >
                    {isFetchingMembers ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : members.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          No active members found.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                          <TextField
                            size="small"
                            placeholder="Search members..."
                            value={memberSearchQuery}
                            onChange={(e) => setMemberSearchQuery(e.target.value)}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1,
                                bgcolor: theme.palette.background.paper,
                                "& fieldset": { borderColor: theme.palette.divider },
                                "&:hover fieldset": { borderColor: theme.palette.primary.main }
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Iconify
                                    icon="eva:search-outline"
                                    width={18}
                                    sx={{ color: theme.palette.text.secondary }}
                                  />
                                </InputAdornment>
                              ),
                              endAdornment: memberSearchQuery && (
                                <InputAdornment position="end">
                                  <Box
                                    onClick={() => setMemberSearchQuery("")}
                                    sx={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      "&:hover": { opacity: 0.7 }
                                    }}
                                  >
                                    <Iconify
                                      icon="eva:close-outline"
                                      width={18}
                                      sx={{ color: theme.palette.text.secondary }}
                                    />
                                  </Box>
                                </InputAdornment>
                              )
                            }}
                            fullWidth
                          />
                        </Box>
                        <TableContainer
                          sx={{
                            maxHeight: expandMembers ? "none" : 200,
                            overflow: "auto",
                            transition: "max-height 0.3s ease"
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {members
                                .filter((m) => {
                                  if (!memberSearchQuery) return true;
                                  const q = memberSearchQuery.toLowerCase();
                                  return (
                                    m.name?.toLowerCase().includes(q) ||
                                    m.role?.toLowerCase().includes(q) ||
                                    m.email?.toLowerCase().includes(q)
                                  );
                                })
                                .map((m) => (
                                  <TableRow key={m.contactId} hover>
                                    <TableCell>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        <Link
                                          href={`/business-insights?phonebookContactId=${m.contactId}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          underline="hover"
                                          variant="body2"
                                          fontWeight={600}
                                        >
                                          {m.name}
                                        </Link>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" color="text.secondary">
                                        {m.role}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{m.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">{formatPhone(m.phones)}</Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Paper>
                </Box>
              )}

              {kind === "Contact" && phonebookContactId && (
                <PastInteractionsModal
                  contactId={phonebookContactId.toString()}
                  open={pastInteractionsOpen}
                  onClose={() => setPastInteractionsOpen(false)}
                />
              )}
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
}
